import { guard } from "@ucast/mongo2js";
import { FieldValues } from "react-hook-form";

import { IExtendableDto, PermissionMap } from "../dto";
import { FEAvailableSystemParamMetadata, IValidationsYupRoot } from "../models";
import { CustomGuard, CustomOperators } from "./guard.validation.helper";
import { hasKeys, isObject } from "./objects.helpers";

export class ValidationHelpers {
  static hasRequiredRule(
    validation: IValidationsYupRoot | null | undefined,
  ): boolean {
    if (!validation) {
      return false;
    }

    return validation.rules?.some(x => x.validation === "required") ?? false;
  }
}

export const getIsShownByCondition = (
  fieldValues: FieldValues,
  conditions: object | null | undefined,
): boolean => {
  if (!conditions || !hasKeys(conditions) || !hasKeys(fieldValues)) {
    return true;
  }

  const conditionsTestObject = guard(conditions);

  if (!conditionsTestObject?.length) {
    return true;
  }

  return conditionsTestObject(fieldValues);
};

export const getIsShownByConditionRenderTemplate = ({
  formValues,
  formVisibleCondition,
  permissions,
  isCheckDtoValuesForVisibleConditions,
  dto,
  isCheckSystemParamValuesForVisibleConditions,
  systemParams,
}: {
  formValues: FieldValues;
  formVisibleCondition: object | null | undefined;
  isCheckDtoValuesForVisibleConditions: boolean;
  dto?: IExtendableDto;
  isCheckSystemParamValuesForVisibleConditions?: boolean;
  systemParams?: FEAvailableSystemParamMetadata;
  permissions?: PermissionMap;
}): boolean => {
  if (isCheckSystemParamValuesForVisibleConditions) {
    if (!systemParams) {
      return false;
    }

    if (
      !formVisibleCondition ||
      !hasKeys(formVisibleCondition) ||
      !hasKeys(formValues)
    ) {
      return true;
    }

    const conditionsTestTemplateObject = CustomGuard(formVisibleCondition);

    if (!conditionsTestTemplateObject?.length) {
      return true;
    }

    return conditionsTestTemplateObject({ ...systemParams, permissions });
  }

  if (isCheckDtoValuesForVisibleConditions) {
    if (!dto) {
      return false;
    }

    if (
      !formVisibleCondition ||
      !hasKeys(formVisibleCondition) ||
      !hasKeys(formValues)
    ) {
      return true;
    }

    const conditionsTestTemplateObject = CustomGuard(formVisibleCondition);

    if (!conditionsTestTemplateObject?.length) {
      return true;
    }

    return conditionsTestTemplateObject({ ...dto, permissions });
  }

  if (
    !formVisibleCondition ||
    !hasKeys(formVisibleCondition) ||
    !hasKeys(formValues)
  ) {
    return true;
  }

  const conditionsTestObject = CustomGuard(formVisibleCondition);

  if (!conditionsTestObject?.length) {
    return true;
  }

  return conditionsTestObject({ ...formValues, permissions });
};

export const getIsGridCellShownByCondition = ({
  gridVisibleCondition,
  rowValues,
  permissions,
}: {
  gridVisibleCondition: object | null | undefined;
  rowValues: Record<string, unknown> | null | undefined;
  permissions?: PermissionMap;
}): boolean => {
  if (!gridVisibleCondition || !hasKeys(gridVisibleCondition)) {
    return true;
  }

  const conditionsTestObject = CustomGuard(gridVisibleCondition);

  if (!conditionsTestObject?.length) {
    return true;
  }

  return conditionsTestObject({ ...(rowValues ?? {}), permissions });
};

export const getIsDisabledByCondition = ({
  fieldValues,
  conditions,
  permissions,
}: {
  fieldValues: FieldValues | unknown;
  conditions: object | undefined;
  permissions?: PermissionMap;
}): boolean => {
  if (!conditions || !hasKeys(conditions) || !hasKeys(fieldValues)) {
    return false;
  }

  const conditionsTestObject = CustomGuard(conditions);

  if (!conditionsTestObject?.length) {
    return true;
  }

  const mergedPermissions = {
    ...(fieldValues as Record<string, unknown>),
    permissions: permissions ?? {},
  };

  return conditionsTestObject(mergedPermissions);
};

export const getIsDisabledByConditionBulk = ({
  selectedRowsValues,
  conditions,
  permissions,
}: {
  selectedRowsValues: IExtendableDto[] | undefined;
  conditions: object | undefined;
  permissions?: PermissionMap;
}): boolean => {
  if (!selectedRowsValues?.length) {
    return false;
  }

  return selectedRowsValues.every(fieldValues =>
    getIsDisabledByCondition({ fieldValues, conditions, permissions }),
  );
};

/**
 * Descending into an unknown $ operator only over-collects, which costs one extra
 * subscription, while missing a field would leave a condition stale. So only the
 * operators whose value is known to be a data payload are skipped.
 */
const isPermissionOperator = (key: string): boolean =>
  key === CustomOperators.PERMISSION_HAS ||
  key === CustomOperators.PERMISSION_HAS_NOT;

const collectConditionFieldNames = (
  condition: unknown,
  fieldNames: Set<string>,
): void => {
  if (Array.isArray(condition)) {
    condition.forEach(item => collectConditionFieldNames(item, fieldNames));
    return;
  }

  if (!isObject(condition)) {
    return;
  }

  Object.entries(condition as Record<string, unknown>).forEach(
    ([key, value]) => {
      // A permission operator carries a data payload ([{ entity, action }]) rather
      // than a nested condition, so its own keys are not form field paths.
      if (isPermissionOperator(key)) {
        return;
      }

      // Logical operators ($or/$and/$not/...) nest further conditions; every other
      // key is a field path, and its value holds only comparison operators.
      if (key.startsWith("$")) {
        collectConditionFieldNames(value, fieldNames);
        return;
      }

      fieldNames.add(key);
    },
  );
};

/**
 * Field paths a mongo-style condition reads. Lets a form subscribe to just the
 * values that can flip a visible/disable condition instead of re-rendering on
 * every keystroke in every field.
 */
export const getConditionFieldNames = (
  conditions: object | null | undefined,
): string[] => {
  if (!hasKeys(conditions)) {
    return [];
  }

  const fieldNames = new Set<string>();
  collectConditionFieldNames(conditions, fieldNames);

  return [...fieldNames];
};
